import React from "react";
import styles from "./PrivacyPolicy.module.css";

const PrivacyPolicy = () => (
  <div className={styles.privacyContainer}>
    <h1 className={styles.heading}>Privacy Policy</h1>
    <p className={styles.intro}>
      <strong>Green Rock Creations Corp.</strong> is committed to protecting
      your privacy and ensuring transparency in how we handle your information.
      This Privacy Policy explains what data we collect, how we use it, your
      rights, and how we keep your information safe. By using our website, you
      trust us with your data—and we take that responsibility seriously.
    </p>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>1. About Us</h2>
      <p>
        Green Rock Creations Corp. is a leader in lab-grown diamonds and fine
        jewelry. We value your trust and are dedicated to ethical, secure, and
        transparent business practices. If you have any questions, please
        contact us at any time.
      </p>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>2. Information We Collect</h2>
      <ul className={styles.list}>
        <li>
          <strong>Personal Data:</strong> Name, email, phone, address, and other
          identifiers you provide when registering, making inquiries, or
          purchasing.
        </li>
        <li>
          <strong>User-Generated Content:</strong> Any content, images, or
          documents you upload (e.g., profile info, business documents,
          reviews).
        </li>
        <li>
          <strong>Usage Data:</strong> Pages visited, time spent, device/browser
          info, IP address, and referral source.
        </li>
        <li>
          <strong>Cookies & Tracking:</strong> We use cookies and similar
          technologies to enhance your experience and analyze site usage.
        </li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>3. How We Use Your Information</h2>
      <ul className={styles.list}>
        <li>To provide, personalize, and improve our services and website</li>
        <li>To process your orders, inquiries, and account management</li>
        <li>To communicate with you about updates, offers, and support</li>
        <li>
          To ensure security, prevent fraud, and comply with legal obligations
        </li>
        <li>To analyze usage and improve our products and services</li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>
        4. User-Generated Content & Uploaded Data
      </h2>
      <ul className={styles.list}>
        <li>
          Content you upload (such as documents, images, or reviews) may be
          visible to authorized users or administrators.
        </li>
        <li>
          We do not claim ownership of your uploaded content, but you grant us a
          license to use it for providing our services.
        </li>
        <li>
          We take reasonable steps to protect your uploaded data, but you are
          responsible for ensuring you have the right to share it.
        </li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>
        5. Cookies & Tracking Technologies
      </h2>
      <ul className={styles.list}>
        <li>
          We use cookies to remember your preferences, keep you logged in, and
          analyze site traffic.
        </li>
        <li>
          You can manage or disable cookies in your browser settings, but some
          features may not work as intended.
        </li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>
        6. Analytics & Third-Party Services
      </h2>
      <ul className={styles.list}>
        <li>
          We use analytics tools (such as Google Analytics) to understand how
          visitors use our site.
        </li>
        <li>
          Third-party services may collect data as described in their own
          privacy policies.
        </li>
        <li>
          We carefully select partners and require them to protect your data.
        </li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>7. How We Share Your Information</h2>
      <ul className={styles.list}>
        <li>
          We <strong>never</strong> sell your personal information.
        </li>
        <li>
          We may share data with trusted partners (e.g., payment processors,
          hosting, analytics) under strict confidentiality.
        </li>
        <li>
          We may disclose information if required by law, to protect our rights,
          or in connection with a business transfer.
        </li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>8. Data Security</h2>
      <ul className={styles.list}>
        <li>
          We use industry-standard security measures (encryption, access
          controls, monitoring) to protect your data.
        </li>
        <li>
          No method of transmission or storage is 100% secure, but we strive to
          safeguard your information.
        </li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>9. Your Rights & Choices</h2>
      <ul className={styles.list}>
        <li>Access, update, or delete your personal data by contacting us</li>
        <li>Opt out of marketing communications at any time</li>
        <li>Manage cookie preferences in your browser</li>
        <li>Request a copy of your data or ask us to restrict processing</li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>10. Data Retention</h2>
      <ul className={styles.list}>
        <li>
          We retain your data only as long as necessary for the purposes
          described in this policy or as required by law.
        </li>
        <li>You may request deletion of your account and data at any time.</li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>11. International Data Transfers</h2>
      <ul className={styles.list}>
        <li>
          Your data may be processed or stored in countries outside your own,
          but we ensure adequate protection and compliance with applicable laws.
        </li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>12. Children's Privacy</h2>
      <ul className={styles.list}>
        <li>Our website is not intended for children under 16.</li>
        <li>
          We do not knowingly collect data from children. If you believe a child
          has provided us data, please contact us for removal.
        </li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>13. Changes to This Policy</h2>
      <ul className={styles.list}>
        <li>
          We may update this Privacy Policy from time to time. Changes will be
          posted on this page with a revised date.
        </li>
      </ul>
    </section>

    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>14. Contact Us</h2>
      <p>
        If you have any questions, requests, or concerns about this Privacy
        Policy or your data, please contact us via our{" "}
        <a className={styles.link} href="/contact">
          Contact page
        </a>
        .
      </p>
    </section>

    <div className={styles.updated}>Last updated: June 2024</div>
  </div>
);

export default PrivacyPolicy;
